import React from 'react';
import {
  TouchableOpacity,
  Text,
  View,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../../theme/useTheme';

// Default icons
const PlusIcon = ({ color = '#FFF', size = 24 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill={color} />
  </Svg>
);

const LocationIcon = ({ color = '#005CBC', size = 24 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path 
      d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM7 9C7 6.24 9.24 4 12 4C14.76 4 17 6.24 17 9C17 11.88 14.12 16.19 12 18.88C9.92 16.21 7 11.85 7 9Z" 
      fill={color}
    />
    <Path 
      d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" 
      fill={color}
    />
  </Svg>
);

export type DLOButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type DLOButtonSize = 'small' | 'medium' | 'large';

export interface DLOButtonProps {
  text: string;
  variant?: DLOButtonVariant;
  size?: DLOButtonSize;
  disabled?: boolean;
  isLoading?: boolean;
  isDark?: boolean;
  iconLeading?: React.ReactNode;
  iconTrailing?: React.ReactNode;
  onPress?: () => void;
  style?: ViewStyle;
  className?: string;
}

// Color constants for better maintainability
const COLORS = {
  // Light theme colors
  light: {
    primary: '#034895',
    primaryHover: '#02365f',
    primaryActive: '#022448',
    secondary: '#FBFCFE',
    text: '#FFF',
    secondaryText: '#034895',
  },
  // Dark theme colors
  dark: {
    primary: '#FBFCFE',
    primaryHover: 'rgba(251, 252, 254, 0.8)',
    primaryActive: 'rgba(251, 252, 254, 0.6)',
    secondary: 'transparent',
    text: 'rgb(39, 39, 39)',
    secondaryText: '#FBFCFE',
  },
  // App-wide dark theme (resolved via useTheme) — brand blue used by the rest
  // of the dark UI, so DLO buttons match e.g. the Filters button.
  appDark: {
    primary: '#3D8BFF',
    primaryHover: '#5C9FFF',
    primaryActive: '#2F73D9',
    secondary: 'transparent',
    text: '#FFF',
    secondaryText: '#3D8BFF',
  },
  // Shared colors
  disabled: {
    primary: '#034895', // Use blue for disabled primary button
    secondary: '#fbfcfe',
    border: '#d0d0d0',
    text: '#a0a0a0',
    textDark: '#999',
    backgroundDark: 'rgb(39, 39, 39)',
  },
} as const;

const DLOButton: React.FC<DLOButtonProps> = ({
  text,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  isLoading = false,
  isDark = false,
  iconLeading,
  iconTrailing,
  onPress,
  style,
  className,
}) => {
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === 'dark';
  const [isHovered, setIsHovered] = React.useState(false);
  const [isPressed, setIsPressed] = React.useState(false);

  // Memoized size styles to prevent unnecessary recalculations
  const sizeStyles = React.useMemo(() => {
    switch (size) {
      case 'small':
        return {
          container: styles.smallContainer,
          padding: styles.smallPadding,
          text: styles.smallText,
          iconSize: 16,
        };
      case 'large':
        return {
          container: styles.largeContainer,
          padding: styles.largePadding,
          text: styles.largeText,
          iconSize: 24,
        };
      default: // medium
        return {
          container: styles.mediumContainer,
          padding: styles.mediumPadding,
          text: styles.mediumText,
          iconSize: 20,
        };
    }
  }, [size]);

  // Memoized variant styles for better performance
  const variantStyles = React.useMemo(() => {
    const theme = isDark ? COLORS.dark : isDarkTheme ? COLORS.appDark : COLORS.light;
    const disabledColors = COLORS.disabled;

    // Helper function to get button state colors
    const getButtonColors = (variant: DLOButtonVariant) => {
      if (disabled) {
        switch (variant) {
          case 'secondary':
            return {
              background: isDark ? 'transparent' : disabledColors.secondary,
              border: isDark ? disabledColors.backgroundDark : disabledColors.border,
              text: isDark ? disabledColors.textDark : disabledColors.text,
            };
          case 'tertiary':
            return {
              background: 'transparent',
              border: 'transparent',
              text: isDark ? disabledColors.textDark : disabledColors.text,
            };
          default: // primary
            if (isDarkTheme && !isDark) {
              // App dark theme: muted grey pill with dim label, instead of a
              // washed-out blue (blue fill at reduced opacity + grey text).
              return {
                background: '#3A3A3A',
                border: 'transparent',
                text: '#8C8C8C',
              };
            }
            return {
              background: isDark ? disabledColors.backgroundDark : disabledColors.primary,
              border: 'transparent',
              text: isDark ? disabledColors.textDark : '#FFFFFF',
            };
        }
      }

      // Interactive states for enabled buttons
      switch (variant) {
        case 'secondary':
          if (isPressed) {
            return {
              background: isDark ? theme.primaryActive : COLORS.light.primaryActive,
              border: theme.secondaryText,
              text: isDark ? theme.text : '#FFFFFF',
            };
          }
          if (isHovered) {
            return {
              background: isDark ? theme.primary : COLORS.light.primaryHover,
              border: theme.secondaryText,
              text: isDark ? theme.text : '#FFFFFF',
            };
          }
          return {
            background: isDark ? theme.secondary : theme.secondary,
            border: theme.secondaryText,
            text: theme.secondaryText,
          };
        case 'tertiary':
          return {
            background: 'transparent',
            border: 'transparent',
            text: theme.secondaryText,
          };
        default: // primary
          if (isPressed) {
            return {
              background: theme.primaryActive,
              border: 'transparent',
              text: isDark ? theme.text : theme.text,
            };
          }
          if (isHovered) {
            return {
              background: theme.primaryHover,
              border: 'transparent',
              text: isDark ? theme.text : theme.text,
            };
          }
          return {
            background: theme.primary,
            border: 'transparent',
            text: isDark ? theme.text : theme.text,
          };
      }
    };

    const colors = getButtonColors(variant);
    const hasWebCursor = Platform.OS === 'web' && disabled;

    return {
      container: {
        backgroundColor: colors.background,
        ...(variant === 'secondary' ? { borderWidth: 1, borderColor: colors.border } : {}),
        ...(hasWebCursor ? { cursor: 'not-allowed' as any } : {}),
      },
      text: { color: colors.text },
      iconColor: colors.text,
    };
  }, [variant, isDark, isDarkTheme, disabled, isPressed, isHovered]);

  const containerStyle = React.useMemo(() => {
    const isAppDarkDisabledPrimary = disabled && isDarkTheme && !isDark && variant === 'primary';
    return [
      styles.baseContainer,
      sizeStyles.container,
      variantStyles.container,
      // In app dark theme the disabled look comes from the grey fill/label, so
      // skip the extra opacity (it made the label nearly invisible).
      disabled && !isAppDarkDisabledPrimary && styles.disabled,
      style,
      // Callers often pass a hard-coded blue backgroundColor via `style`; keep
      // the greyed-out fill on top so a disabled button never looks active.
      isAppDarkDisabledPrimary && { backgroundColor: variantStyles.container.backgroundColor },
    ];
  }, [sizeStyles.container, variantStyles.container, disabled, style, isDarkTheme, isDark, variant]);

  const textStyle = React.useMemo(
    () => [styles.baseText, sizeStyles.text, variantStyles.text],
    [sizeStyles.text, variantStyles.text]
  );

  // Optimized icon rendering with memoization
  const renderIcon = React.useCallback((icon: React.ReactNode, isLeading: boolean) => {
    if (!icon) return null;

    // If it's a default icon component, clone it with proper color
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<any>, {
        color: variantStyles.iconColor,
        size: sizeStyles.iconSize,
      });
    }

    return icon;
  }, [variantStyles.iconColor, sizeStyles.iconSize]);

  // Optimized event handlers
  const handlePressIn = React.useCallback(() => {
    if (!disabled && !isLoading) {
      setIsPressed(true);
    }
  }, [disabled, isLoading]);

  const handlePressOut = React.useCallback(() => {
    setIsPressed(false);
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    if (!disabled && !isLoading) {
      setIsHovered(true);
    }
  }, [disabled, isLoading]);

  const handleMouseLeave = React.useCallback(() => {
    setIsHovered(false);
    setIsPressed(false);
  }, []);

  // Web-specific props
  const webProps = React.useMemo(() => {
    return Platform.OS === 'web' ? {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    } : {};
  }, [handleMouseEnter, handleMouseLeave]);

  return (
    <TouchableOpacity
      style={containerStyle}
      onPress={onPress}
      disabled={disabled || isLoading}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityState={{ disabled: disabled || isLoading }}
      accessibilityLabel={text}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      {...webProps}
    >
      <View style={[styles.content, sizeStyles.padding]}>
        {isLoading ? (
          <ActivityIndicator 
            size={sizeStyles.iconSize} 
            color={variantStyles.iconColor} 
          />
        ) : (
          <>
            {iconLeading && renderIcon(iconLeading, true)}
            <Text style={textStyle} numberOfLines={1}>
              {text}
            </Text>
            {iconTrailing && renderIcon(iconTrailing, false)}
          </>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  baseContainer: {
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 4,
  },
  baseText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '700',
    textAlign: 'center',
    maxWidth: 150,
  },
  // Size variants
  smallContainer: {
    height: 28,
    minWidth: 60,
  },
  smallPadding: {
    paddingVertical: 2,
    paddingHorizontal: 12,
  },
  smallText: {
    fontSize: 12,
    lineHeight: 16,
  },
  mediumContainer: {
    height: 32,
    minWidth: 80,
  },
  mediumPadding: {
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  mediumText: {
    fontSize: 14,
    lineHeight: 20,
  },
  largeContainer: {
    height: 40,
    minWidth: 120,
  },
  largePadding: {
    paddingVertical: 8,
    paddingHorizontal: 24,
  },
  largeText: {
    fontSize: 16,
    lineHeight: 22,
  },
  disabled: {
    opacity: 0.65,
  },
});

// Export default icons for convenience
export { PlusIcon, LocationIcon };
export default DLOButton;
